/** @file Drags one socket or handle point; socket drags preview the socket bone, handle drags only the point. */
import { gripSetup } from '@pose2d/commands/socket/gripSetup';
import { viewCamera } from '@pose2d/editor-geometry/viewCamera';
import { affine } from '@pose2d/math/affine';
import type { Vec2 } from '@pose2d/math/types';
import { OVERLAY_STYLE } from '@pose2d/overlay/overlayStyle';
import type { ToolContext, ToolGesture } from '../../panels/viewport/viewportTypes';
import { worldAt } from '../../panels/viewport/worldAt';
import type { PendingCommand } from '../../stores/types';
import { roundValue } from '../transform/roundValue';
import { snappedWorld } from '../transform/snappedWorld';
import type { PointTarget } from './pointTargets';

/** Preview of the socket bone for the moved point; a = b previews nothing (gripSetup refuses it). */
function previewSocket(ctx: ToolContext, t: Extract<PointTarget, { kind: 'socket' }>, local: Vec2): void {
  try {
    const { setup } = gripSetup(t.which === 'a' ? local : t.a, t.which === 'b' ? local : t.b);
    ctx.setPreview(new Map([[t.bone, { x: setup.x, y: setup.y, rotation: setup.rotation }]]));
  } catch {
    ctx.setPreview(new Map());
  }
}

/** socket.setPoints with the moved point (parent-bone space), or attachment.setHandle with the whole handle (image px). */
function commandOf(t: PointTarget, local: Vec2): PendingCommand {
  if (t.kind === 'socket') return { name: 'socket.setPoints', input: { target: t.socket, [t.which]: [roundValue(local[0]), roundValue(local[1])] }, ids: [] };
  return { name: 'attachment.setHandle', input: { target: t.attachment, value: { ...t.handle, [t.which]: [roundValue(local[0], 2), roundValue(local[1], 2)] } }, ids: [] };
}

/** null (notice viewport.setupOnly) for socket points outside Setup mode (R-E1-09); a click without a move commits nothing. */
export function pointGesture(ctx: ToolContext, t: PointTarget): ToolGesture | null {
  if (t.kind === 'socket' && ctx.tool.mode !== 'setup') {
    ctx.notify({ kind: 'info', key: 'viewport.setupOnly' });
    return null;
  }
  let local: Vec2 | null = null;
  return {
    move(p) {
      const w = snappedWorld(ctx, p, worldAt(ctx, p));
      local = affine.apply(t.toLocal, w[0], w[1], [0, 0]);
      if (t.kind === 'socket') previewSocket(ctx, t, local);
      ctx.setGizmo({ handles: [], active: null, points: [{ at: viewCamera.worldToScreen(ctx.camera, w[0], w[1], [0, 0]), color: OVERLAY_STYLE.joint }], lines: [] });
    },
    release: () => (local === null ? [] : [commandOf(t, local)]),
    cancel() {},
  };
}
